import { Injectable } from '@angular/core';
import { EbwModalService } from '@ebw-monorepo/modal';
import { UtilsService } from '@ebw-monorepo/utils';
import { getCenter } from 'ol/extent';
import Feature from 'ol/Feature';
import { GeoJSON, KML } from 'ol/format';
import LineString from 'ol/geom/LineString';
import Point from 'ol/geom/Point';
import { Circle as CircleStyle, Fill, Icon, Stroke, Style } from 'ol/style';
import JSZip from 'jszip';
import { catchError, firstValueFrom, lastValueFrom, map, Observable, throwError } from 'rxjs';
import { API_URLS, ApiUrlService } from '../../../../utils/src/lib/services/api-url.service';
import { OtdrStatusEnum } from '../components/sidebar/otdr/enum/otdr-status.enum';
import { OtdrRequestParams, OtdrResponse } from '../components/sidebar/otdr/model/otdr.model';
import { FlatNode, TreeNode } from '../models/ebw-tree.model';
import { ExportService } from './export.service';
import { FeatureSelectService } from './feature-select.service';
import { LayerStylesService } from './layer-styles.service';
import { MapService } from './map.service';
import { SnackbarService } from './snackbar.service';
import { safeJsonParse } from '@ebw-monorepo/microservices-helpers/utils/parser';

interface RelatedPdt {
  urn: string;
  label: string;
}

interface TracciatoIRUProperties {
  codiceAltroOperatore: string;
  relatedPdt: RelatedPdt[];
}

@Injectable({
  providedIn: 'root'
})
export class TelcoService {
  private telcoConfig: {
    otdr: { assetTypesAllowedToStartOtdr: string; assetTypeSelectableForOtdrPath: string };
    analisiSchiacciamenti: { assetTypesAllowed: string };
    toolFattibilita: {
      includedAssetTypesAllowed: string;
      excludedAssetTypesAllowed: string;
      pointsAssetTypesAllowed: string;
      hit_radius: number;
    };
  };

  constructor(
    private _exportService: ExportService,
    private _featureSelectService: FeatureSelectService,
    private _layerStylesService: LayerStylesService,
    private _mapService: MapService,
    private _snackbarService: SnackbarService,
    private _utilsService: UtilsService,
    private _apiUrlService: ApiUrlService,
    private _modalService: EbwModalService
  ) {}

  // --------------------------------------------------------------------------------------------------------------------

  setTelcoConfig(config: any) {
    this.telcoConfig = config;
  }

  // --------------------------------------------------------------------------------------------------------------------

  /**
   * Config del tool fattibilità (includedAssetTypesAllowed/excludedAssetTypesAllowed/hit_radius), usata da FattibilitaService.
   * Resta qui invece che nel servizio fattibilità perché telcoConfig è popolato da MapWrapperComponent
   * (sempre attivo, fuori da FattibilitaModule): solo un servizio root-scoped come TelcoService è
   * raggiungibile da lì, mentre FattibilitaService è provided in FattibilitaModule.providers e quindi
   * visibile solo ai componenti di quel modulo — MapWrapperComponent non potrebbe iniettarlo.
   */
  getToolFattibilitaConfig():
    | { includedAssetTypesAllowed: string; excludedAssetTypesAllowed: string; pointsAssetTypesAllowed: string; hit_radius: number }
    | undefined {
    return this.telcoConfig?.toolFattibilita;
  }

  // --------------------------------------------------------------------------------

  /**
   * Api per recupero struttura ad albero di un elemento selezionato su mappa
   * @param urn urn dell'elemento padre dell'albero
   * @param type tipo di albero richiesto a gss, opzionale
   * @returns
   */
  async getTreeStructure(urn: string, type?: string, notRecursive = false) {
    try {
      let url = `${this._apiUrlService.createUrl(API_URLS.MAP.TELCO.STUCTURE)}?root_obj=${urn}`;
      url += type ? `&type=${type}` : '';
      url += !!notRecursive ? `&not_recursive=${notRecursive}` : '';
      const request = this._utilsService.getRequest(url, {});
      return await firstValueFrom(request);
    } catch (error) {
      console.error('[TelcoService][getTreeStructure]', error);
      return null;
    }
  }

  // --------------------------------------------------------------------------------------------------------------------

  otdrSearch(term: string) {
    const assetTypes = this.telcoConfig.otdr?.assetTypesAllowedToStartOtdr;
    return this.search(term, assetTypes);
  }

  // --------------------------------------------------------------------------------------------------------------------

  analisiSchiacciamentiSearch(term: string) {
    const assetTypes = this.telcoConfig.analisiSchiacciamenti?.assetTypesAllowed;
    return this.search(term, assetTypes);
  }

  // --------------------------------------------------------------------------------------------------------------------

  caviRaccordoSearch(term: string) {
    const assetTypes = 'Edificio';
    return this.search(term, assetTypes);
  }

  // --------------------------------------------------------------------------------------------------------------------

  /**
   * Ricerca generica per external_name/tipo di asset. Pubblico perché FattibilitaService lo usa per la
   * ricerca testuale degli elementi inclusi/esclusi (stesso filtro condiviso con la selezione da mappa).
   * @param term frase da ricercare
   * @param filterTypes tipi di asset da ricercare concatenari da virgola. es. 'Link,Circuit'
   * @returns
   */
  search(term: string, filterTypes: string = ''): Observable<any> {
    let filterQuery = '';
    // Se vengono passati dei tipi di asset li includo nella query
    if (filterTypes) {
      filterQuery = `&filter=type:${filterTypes}`;
    }
    const url = `${this._apiUrlService.createUrl(API_URLS.MAP.CORE.SEARCH)}?term=${term}${filterQuery}`;
    const request = this._utilsService.getRequest(url, {});
    return request.pipe(
      map((res: any) => {
        if (res.error) {
          throw Error(res.error_description);
        }
        if (res?.response?.docs) {
          return res.response.docs.map((_) => ({ desc: _.description, type: _.type, urns: _.url }));
        }
        return [];
      }),
      catchError((error) => throwError(() => error))
    );
  }

  // --------------------------------------------------------------------------------------------------------------------

  async startOtdr(params: OtdrRequestParams): Promise<OtdrResponse> {
    const { distance, device_urn, object } = params;
    let url = `${this._apiUrlService.createUrl(API_URLS.MAP.TELCO.OTDR_START)}?object=${object}&distance=${distance}&device_urn=${device_urn}`;
    try {
      const request = this._utilsService.getRequest(url, {});
      const response = await lastValueFrom(request);

      const parsedResponse = response as OtdrResponse;
      if (parsedResponse.properties.status === OtdrStatusEnum.ko) {
        // Se la procedura di TDR
        parsedResponse.properties.breakpoints = parsedResponse.properties.breakpoints.map((p) => ({
          ...p,
          coordinates: this._mapService.transformGeometry(p.coordinates, '3857to4326')
        }));
      }

      return parsedResponse;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  // --------------------------------------------------------------------------------------------------------------------

  async startOtdrDefault(params: OtdrRequestParams): Promise<OtdrResponse> {
    let url = `${this._apiUrlService.createUrl(API_URLS.MAP.TELCO.OTDR_START_DEFAULT)}`;
    // deep copy per non modificare l'oggetto in ingresso ed avere problemi per le chiamate successive
    try {
      let body = { ...params };
      body.object.nodes = JSON.stringify(body.object.nodes);
      const request = this._utilsService.postRequest(url, body, false, {}, 'application/json');
      const parsedResponse = (await lastValueFrom(request)) as OtdrResponse;

      if (parsedResponse.properties.status === OtdrStatusEnum.ko) {
        // Se la procedura di TDR
        parsedResponse.properties.breakpoints = parsedResponse.properties.breakpoints.map((p) => ({
          ...p,
          coordinates: this._mapService.transformGeometry(p.coordinates, '3857to4326')
        }));
      }
      return parsedResponse;
    } catch (error) {
      console.error('[telcoService][startOtdrDefault] ', error);
      return null;
    }
  }

  // --------------------------------------------------------------------------------------------------------------------

  centerMapOnGeometry(geometry: any, styleName = null, from4326To3857 = false) {
    let geom = { ...geometry };
    if (from4326To3857) {
      geom.coordinates = this._mapService.transformGeometryCordsFrom4326To3857(geometry.coordinates);
    }
    const format = new GeoJSON();
    const style = this._layerStylesService.buildFeatureStyleFromId(styleName);
    // nosemgrep: nodejs_scan.javascript-crypto-rule-node_insecure_random_generator
    const id = `id_${Math.random()}`;
    this._mapService.addFeatureToTemporaryLayer(geom, style, true, false, id, styleName);
    const options = {
      dataProjection: MapService.EPSG_3857_KEY,
      featureProjection: MapService.EPSG_3857_KEY
    };
    const geomExtent = format.readGeometry(geom, options).getExtent();

    // prepare a default size in case the map dimensions are 0
    const size = this._mapService.openLayersMap.getSize();

    if (size[0] === 0) {
      size[0] = 500;
    }
    if (size[1] === 0) {
      size[1] = 500;
    }
    this._mapService.openLayersMap.getView().fit(geomExtent, { size: size });
  }

  // --------------------------------------------------------------------------------------------------------------------

  clearOtdrTemporaryLayer() {
    this._mapService.clearAllFeaturesFromTemporaryLayer(true);
  }

  // --------------------------------------------------------------------------------------------------------------------

  /**
   * Controlla se l'asset in questione è un selezionabile come elemento per il percorso OTDR
   * @param assetType tipo dell'asset, riconducibile al suo external_name
   */
  isAssetSelectableBasedOnType = (assetType: string): boolean => this.telcoConfig?.otdr?.assetTypeSelectableForOtdrPath?.includes(assetType);

  // --------------------------------------------------------------------------------------------------------------------

  /**
   * Controlla se all'asset in questione è permesso di far partire una procedura di OTDR
   * @param assetType tipo dell'asset, riconducibile al suo external_name
   */
  isAssetTypesAllowedToStartOtdr = (assetType: string): boolean =>
    this.telcoConfig?.otdr?.assetTypesAllowedToStartOtdr.split(',').some((t) => assetType.includes(t));

  // --------------------------------------------------------------------------------------------------------------------

  /**
   * Controlla se all'asset in questione è permesso di far partire una procedura export del percorso dei circuiti in formato kmz
   * @param urn urn dell'asset
   */
  isAssetAllowedToDownloadKmzCircuiti = (urn: string): boolean => {
    // tipologie di asset su cui permettere il download di un file .kmz
    const allowedTypes = ['fw_link', 'fw_circuit'];
    return allowedTypes.some((type) => urn.includes(type));
  };

  // --------------------------------------------------------------------------------------------------------------------

  /**
   * Controlla se all'asset in questione è permesso di far partire l'export del percorso di un segmento ottico
   * @param urn urn dell'asset
   */
  isAssetAllowedToDownloadCsvPercorso = (urn: string): boolean => {
    const allowedTypes = ['fw_link'];
    return allowedTypes.some((type) => urn.includes(type));
  };

  // --------------------------------------------------------------------------------------------------------------------

  isAssetAllowedToViewRaccordi = (urn: string): boolean => {
    const allowedTypes = ['building'];
    return allowedTypes.some((type) => urn.includes(type));
  };

  // --------------------------------------------------------------------------------------------------------------------

  isAssetTracciatoIRU = (urn: string): boolean => {
    const allowedTypes = ['tratta_iru'];
    return allowedTypes.some((type) => urn.includes(type));
  };

  // --------------------------------------------------------------------------------------------------------------------

  /**
   * Copia il nome del node negli appunti (Ctrl + C)
   * @param name nome asset
   */
  copyToClipboard = (name: string) => {
    if (!name) {
      const message = 'Nessun elemento da copiare';
      this._snackbarService.open(message);
      return;
    }

    const clipboard = navigator.clipboard;
    if (!clipboard) {
      const message = "Sito non sicuro, impossibile copiare l'elemento";
      this._snackbarService.open(message);
      return;
    }

    clipboard
      .writeText(name)
      .then(() => {
        const message = 'Copiato negli appunti!';
        this._snackbarService.open(message);
      })
      .catch((error) => {
        const message = 'Errore durante la copia';
        this._snackbarService.open(message);
        console.error(error);
      });
  };

  // --------------------------------------------------------------------------------------------------------

  async exportKmz(urn: string) {
    const baseUrl = this._apiUrlService.createUrl(API_URLS.MAP.TELCO.EXPORT_KMZ);
    const url = `${baseUrl}/${urn}`;
    try {
      const request = this._utilsService.getRequest(url);
      await lastValueFrom(request);
      const message = 'Download avviato. Riceverai una notifica al termine.';
      this._snackbarService.open(message);
    } catch (error) {
      this._snackbarService.open(error.message);
      console.error('[TelcoService][exportKmz] ', error.message);
    }
  }

  // --------------------------------------------------------------------------------

  async downloadTreeStructure(node: FlatNode, type: 'csv' | 'html') {
    // estraggo id da urn asset padre
    const idParentNode = node.urn.split('Z').pop();
    // tolgo spazi dal nome
    const nodeType = node.type.replace('.', '').replace(/ /g, '_');
    // compongo il nome del file
    const timestamp = this.getTimestampString();
    const filename = `${nodeType}_${idParentNode}_${timestamp}`;
    // recupero la struttura dell'elemento
    const treeStructure = await this.getTreeStructure(node.urn);
    // ritorno la struttura voluta dal cliente Fastweb
    const assets = treeStructure.children.map((childNode: TreeNode) => {
      const id = childNode.obj_urn.toString().split('Z').pop();

      let infoObj = {};

      if (type != 'csv') {
        switch (childNode.obj_type) {
          default:
            infoObj = childNode.obj_desc.split(';').reduce((acc, item, index) => {
              acc[`colonna_${index}`] = item.trim();
              return acc;
            }, {} as Record<string, string>);
            break;
        }

        return { [`id_${nodeType}`]: idParentNode, id, ...infoObj };
      }

      infoObj = childNode.obj_desc;
      return { [`id_${nodeType}`]: idParentNode, id, data: infoObj };
    });
    this._exportService.export(type, assets, filename);
  }

  // --------------------------------------------------------------------------------

  async downloadExportReportPercorso(urn: string, type: 'csv' | 'pdf' | 'html') {
    const tree = await this.getTreeStructure(urn, 'link_devices');

    let selectByUrnsSegmentoResponse = await firstValueFrom(
      this._utilsService.postRequest(this._apiUrlService.createUrl(API_URLS.MAP.CORE.SELECT_BY_URN), { urns: urn }, false)
    );

    let fwSegmentFeature = new GeoJSON().readFeatures(selectByUrnsSegmentoResponse.result)[0];
    let fwSegmentProps = null;
    let headers: { title: string; data: { label: string; desc: string }[] } | {} = {};
    if (fwSegmentFeature) {
      fwSegmentProps = fwSegmentFeature.getProperties();
    }
    let fwLinkName = tree.obj_desc;
    let filename = `Report_Segmento_${fwLinkName}`;
    // estraggo id da urn asset padre
    let idParentNode = urn.split('Z').pop();
    // tolgo spazi dal nome
    let nodeType = tree.obj_type.replace('.', '').replace(/ /g, '_');
    if (fwSegmentProps) {
      // Potrebbe non essere associato alcun circuito al segmento
      let fwCircuit = fwSegmentProps.fw_circuits[0];
      let fwCircuitProps = null;
      if (!!fwCircuit) {
        fwCircuit = safeJsonParse(fwCircuit);
      }
      if (fwCircuit?.urn) {
        const requestCircuito = this._utilsService.postRequest(
          this._apiUrlService.createUrl(API_URLS.MAP.CORE.SELECT_BY_URN),
          { urns: fwCircuit.urn },
          false
        );
        const selectByUrnsCircuitResponse = await firstValueFrom(requestCircuito);
        let fwCircuitFeature = new GeoJSON().readFeatures(selectByUrnsCircuitResponse.result)[0];
        if (fwCircuitFeature) {
          fwCircuitProps = fwCircuitFeature.getProperties();
        }
      }
      headers[0] = {
        title: !!fwCircuit?.label ? 'Circuito - ' + fwCircuit.label : 'Circuito non associato',
        data: [
          {
            title: 'Stato',
            desc: fwCircuitProps?.construction_status || '-'
          },
          {
            title: 'Categoria',
            desc: fwCircuitProps?.category || '-'
          },
          {
            title: 'Tipo',
            desc: fwCircuitProps?.type || '-'
          },
          {
            title: 'Data pianificazione',
            desc: fwCircuitProps?.planned || '-'
          },
          {
            title: 'Data attivazione',
            desc: fwCircuitProps?.activated || '-'
          },
          {
            title: 'Note',
            desc: fwCircuitProps?.note || '-'
          }
        ]
      };
      headers[1] = {
        title: 'Segmento - ' + fwSegmentProps?.name,
        data: [
          {
            title: `Id ${nodeType}`,
            desc: idParentNode
          },
          {
            title: 'RC',
            desc: fwSegmentProps?.rc || '-'
          },
          {
            title: 'Codice di servizio',
            desc: fwSegmentProps?.codice_servizio_u || '-'
          }
        ]
      };
    }
    // compongo il nome del file
    // recupero la struttura dell'elemento
    // ritorno la struttura voluta dal cliente Fastweb
    const assets = tree.children.map((childNode: TreeNode) => {
      const id = childNode.obj_urn.toString().split('Z').pop();
      let infoObj = {};
      //codice per dividere i valori separati con ; in singole colonne nominate colonna_{index}
      if (type != 'csv') {
        switch (childNode.obj_type) {
          case 'Port':
            infoObj = { ['colonna_0']: childNode.obj_desc };
            break;
          case 'Giunto':
            const match = childNode.obj_desc.match(/Lat:\s*([\d.,]+);?\s*Lon:\s*([\d.,]+)/);
            let lat = '0';
            let lon = '0';
            if (match) {
              lat = match[1].replace(',', '.');
              lon = match[2].replace(',', '.');
            }
            infoObj = childNode.obj_desc.split(';').reduce((acc, item, index) => {
              if (index === 0) {
                acc[`colonna_${index}`] = `<a href="https://www.google.it/maps/place/${lat},${lon}" target="_blank">${item.trim()}</a>`;
              } else {
                acc[`colonna_${index}`] = item.trim();
              }
              return acc;
            }, {} as Record<string, string>);
            break;
          default:
            infoObj = childNode.obj_desc.split(';').reduce((acc, item, index) => {
              acc[`colonna_${index}`] = item.trim();
              return acc;
            }, {} as Record<string, string>);
            break;
        }
        return { id, ...infoObj };
      }
      infoObj = childNode.obj_desc;
      return { id, data: infoObj };
    });
    this._exportService.export(type, assets, filename, headers);
  }

  // --------------------------------------------------------------------------------

  async downloadExportLunghezzeCavo(urn: string, type: 'csv' | 'pdf' | 'html') {
    const tree = await this.getTreeStructure(urn, 'report_lunghezze_cavi');

    let result = [];
    function recursiveFlatten(node: TreeNode) {
      result.push(node);
      if (node.children && node.children.length > 0) {
        node.children.forEach((child) => recursiveFlatten(child));
      }
    }
    // Inizia con il nodo principale, creo un array con tutti gli elementi allo stesso livello
    recursiveFlatten(tree);
    // compongo il nome del file
    const filename = `Report_Lunghezze_Cavo_${tree.obj_desc}`;
    // estraggo id da urn asset
    const idParentNode = urn.split('Z').pop();
    // tolgo spazi dal nome
    const nodeType = tree.obj_type.replace('.', '').replace(/\s/g, '_');
    // elimino elemento root
    result.shift();
    // ritorno la struttura voluta dal cliente Fastweb
    const assets = result.map((childNode: TreeNode) => {
      const id = childNode.obj_urn.toString().split('Z').pop();
      const tipo = childNode.obj_type;
      const description = childNode.obj_desc
        .replace(' ', '_')
        .replace(/\t/g, ' ')
        .replace(/\s{2,}/g, ' ');
      const isCavo = tipo === 'Cavo';
      const nome = isCavo ? '' : description;
      const proprietario = isCavo ? description.split(' ')[0].replace('_', ' ') : '';
      const inizio = isCavo ? `${description.split(' ')[1]} m` : '';
      const fine = isCavo ? `${description.split(' ')[2]} m` : '';

      return { [`id_${nodeType}`]: idParentNode, id, tipo, nome, proprietario, inizio, fine };
    });

    this._exportService.export(type, assets, filename);
  }

  // --------------------------------------------------------------------------------

  async goTo(urn: string) {
    try {
      this._mapService.clearAllFeaturesFromTemporaryLayer(true);
      const response = await this._featureSelectService.selectByUrns([urn]);
      let feature = response?.result?.features[0];
      if (!feature.geometry) {
        const geometry = await this._featureSelectService.getPlacedAssetGeometry(feature);
        feature.geometry = geometry;
      }
      if (feature.geometry) {
        feature.geometry.coordinates = this._mapService.transformGeometryCordsFrom4326To3857(feature.geometry.coordinates);
        //centro la mappa sulla geometry
        this._mapService.centerMapOnGeometry(feature.geometry);
        this._mapService.addFeatureToTemporaryLayer(feature.geometry, null, true);
        return;
      }
      this._snackbarService.open(`Nessuna geometria per l'oggetto selezionato`);
    } catch (error) {
      console.error('[TreeComponent][goTo]', error);
    }
  }

  // --------------------------------------------------------------------------------

  getFwContractsDocumentUrl(id: string) {
    const url = this._apiUrlService.createUrl(API_URLS.MAP.TELCO.SHOW_CONTRACT_DOCUMENT);
    const request = this._utilsService.getRequest(`${url}?id=${id}`, {});
    return request.pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * Genera un timestamp in stringa
   * @returns {string} // es "04_11_2025__15_07"
   */
  getTimestampString(): string {
    const now = new Date();
    const pad = (n: number): string => n.toString().padStart(2, '0');
    const day = pad(now.getDate());
    const month = pad(now.getMonth() + 1);
    const year = now.getFullYear();
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    return `${day}_${month}_${year}__${hours}_${minutes}`;
  }

  async downloadConnectivity(urn: string, connectivityType: string, type: 'csv') {
    try {
      let url = `${this._apiUrlService.createUrl(API_URLS.MAP.TELCO.REPORT_CONNECTIVITY)}?urn=${urn}&type=${connectivityType}`;
      const request = this._utilsService.getRequest(url, {});
      const response = await firstValueFrom(request);

      this._modalService.openSuccessModal({
        title: 'Operazione iniziata',
        content: response.message
      });
    } catch (error) {
      this._modalService.openErrorModal({
        title: 'Errore',
        content: 'Errore avvio generazione connectivity'
      });
    }
  }

  async getTracciatoIRUProperties(urn: string): Promise<TracciatoIRUProperties> {
    try {
      const selectByUrnsResponse = await firstValueFrom(
        this._utilsService.postRequest(this._apiUrlService.createUrl(API_URLS.MAP.CORE.SELECT_BY_URN), { urns: urn }, false)
      );
      const feature = new GeoJSON().readFeatures(selectByUrnsResponse.result)[0];
      const properties = feature?.getProperties();
      if (!properties) {
        return null;
      }
      const relatedPdt: RelatedPdt[] = (properties.related_pdt ?? []).map((entry: string) => safeJsonParse<RelatedPdt>(entry));
      if (relatedPdt.some((pdt) => pdt === null)) {
        throw new Error('related_pdt non valido');
      }
      return { codiceAltroOperatore: properties.codice_altro_operatore, relatedPdt };
    } catch (error) {
      console.error('[TelcoService][getTracciatoIRUProperties]', error);
      this._modalService.openErrorModal({
        title: 'Errore',
        content: 'Errore recupero proprietà tracciato IRU'
      });
      return null;
    }
  }

  // --------------------------------------------------------------------------------

  /**
   * Recupera il tracciato IRU (pdf/kmz) di un asset, con fallback lato client per il kmz: se GSS non
   * ha il kmz (risposta 502 dal backend), viene generato al volo un kmz con il segmento tra i due related_pdt.
   */
  async getTracciatoIRUFile(urn: string, type: 'pdf' | 'kmz'): Promise<{ blob: Blob; codiceAltroOperatore: string }> {
    const properties = await this.getTracciatoIRUProperties(urn);
    if (!properties) {
      return null;
    }

    let blob = await this.fetchTracciatoIRU(properties.codiceAltroOperatore, type);

    if (!blob && type === 'kmz') {
      blob = await this.buildFallbackKmz(properties.relatedPdt);
    }

    if (!blob) {
      if (type === 'pdf') {
        this._modalService.openErrorModal({
          title: 'Errore',
          content: 'Verbale OLO non trovato'
        });
      }
      return null;
    }

    return { blob, codiceAltroOperatore: properties.codiceAltroOperatore };
  }

  // --------------------------------------------------------------------------------

  private async fetchTracciatoIRU(filename: string, type: 'pdf' | 'kmz'): Promise<Blob> {
    try {
      const url = `${this._apiUrlService.createUrl(API_URLS.MAP.TELCO.TRACCIATO_IRU)}?filename=${encodeURIComponent(filename)}.${type}`;
      const request = this._utilsService.getRequest(url, {}, { responseType: 'blob' });
      return await firstValueFrom(request);
    } catch (error) {
      console.error('[TelcoService][fetchTracciatoIRU]', error);
      // 502 = il backend l'ha già segnalato come "file non disponibile da GSS": per il kmz esiste un fallback,
      // quindi non è ancora un errore da mostrare all'utente
      if (error?.status !== 502) {
        this._modalService.openErrorModal({
          title: 'Errore',
          content: 'Errore recupero tracciato IRU'
        });
      }
      return null;
    }
  }

  // --------------------------------------------------------------------------------

  /**
   * Genera un kmz al volo con la linea che collega i due punti di terminazione (related_pdt) del tracciato IRU,
   * usato quando GSS non ha ancora il kmz reale.
   */
  private async buildFallbackKmz(relatedPdt: RelatedPdt[]): Promise<Blob> {
    try {
      if (relatedPdt?.length !== 2) {
        throw new Error(`related_pdt deve contenere esattamente due punti, trovati ${relatedPdt?.length ?? 0}`);
      }

      const selectByUrnsResponse = await this._featureSelectService.selectByUrns(relatedPdt.map((pdt) => pdt.urn));
      const features = selectByUrnsResponse?.result?.features ?? [];

      const coordinates = relatedPdt.map(({ urn, label }) => {
        const geometry = features.find((f: any) => f.id === urn)?.geometry;
        if (!geometry) {
          throw new Error(`Geometria non disponibile per "${label}" (${urn})`);
        }
        return this.geometryToPointCoordinate(geometry);
      });

      const lineFeature = new Feature({ geometry: new LineString(coordinates) });
      lineFeature.setStyle(
        new Style({
          stroke: new Stroke({ color: '#0000ff', width: 5 })
        })
      );

      const pointStyle = new Style({
        image: new Icon({
          src: this.buildCircleIconDataUri('#ff0000', 8)
        })
      });
      const pointFeatures = coordinates.map((coordinate) => {
        const feature = new Feature({ geometry: new Point(coordinate) });
        feature.setStyle(pointStyle);
        return feature;
      });

      const kml = new KML().writeFeatures([lineFeature, ...pointFeatures], {
        featureProjection: 'EPSG:4326',
        dataProjection: 'EPSG:4326'
      });

      const zip = new JSZip();
      zip.file('doc.kml', kml);
      return await zip.generateAsync({ type: 'blob' });
    } catch (error) {
      console.error('[TelcoService][buildFallbackKmz]', error);
      this._modalService.openErrorModal({
        title: 'Errore',
        content: 'Impossibile generare il tracciato IRU: geometrie di riferimento non disponibili'
      });
      return null;
    }
  }

  // --------------------------------------------------------------------------------

  /**
   * Disegna un cerchio pieno su un canvas e lo restituisce come data uri, da usare come icona di un IconStyle KML
   * (il formato KML non supporta stili vettoriali per i punti, richiede sempre un'immagine).
   */
  private buildCircleIconDataUri(color: string, radius: number): string {
    const size = radius * 2;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    context.beginPath();
    context.arc(radius, radius, radius, 0, 2 * Math.PI);
    context.fillStyle = color;
    context.fill();
    return canvas.toDataURL('image/png');
  }

  // --------------------------------------------------------------------------------

  /**
   * Estrae un punto rappresentativo da una geometria GeoJSON in EPSG:4326 (coordinate del Point,
   * oppure centro dell'extent per ogni altro tipo di geometria).
   */
  private geometryToPointCoordinate(geometry: { type: string; coordinates: any }): number[] {
    if (geometry.type === 'Point') {
      return geometry.coordinates;
    }
    const olGeometry = new GeoJSON().readGeometry(geometry, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:4326' });
    return getCenter(olGeometry.getExtent());
  }

  // --------------------------------------------------------------------------------

  downloadFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = filename;
    downloadLink.click();
    // il revoke va posticipato: alcuni browser avviano il download in modo asincrono dopo click()
    setTimeout(() => window.URL.revokeObjectURL(url));
  }

  // --------------------------------------------------------------------------------

  async showKmzOnMap(blob: Blob) {
    try {
      const zip = await JSZip.loadAsync(blob);
      const kmlFile = Object.values(zip.files).find((f) => !f.dir && f.name.toLowerCase().endsWith('.kml'));
      if (!kmlFile) {
        throw new Error('Nessun file .kml trovato nel KMZ');
      }
      const kml = await kmlFile.async('string');
      const features = new KML({ extractStyles: true }).readFeatures(kml, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857'
      });
      // sostituisco il tracciato precedente invece di accumularlo ad ogni click su "Mostra"
      this.clearTracciatoIRUFromMap();
      const defaultPointStyle = new Style({
        image: new CircleStyle({
          radius: 6,
          fill: new Fill({ color: '#ff0000' }),
          stroke: new Stroke({ color: '#ffffff', width: 1 })
        })
      });
      const defaultLineStyle = new Style({
        stroke: new Stroke({ color: '#0000ff', width: 5 })
      });
      features.forEach((feature) => {
        const geometry = feature.getGeometry();
        // uso lo stile incluso nel kmz quando presente; altrimenti punto rosso / linea blu di default
        const style = feature.getStyle() ?? (geometry.getType() === 'Point' ? defaultPointStyle : defaultLineStyle);
        this._mapService.addFeatureToTemporaryLayer(geometry, style, true, true);
      });
    } catch (error) {
      console.error('[TelcoService][showKmzOnMap]', error);
      this._modalService.openErrorModal({
        title: 'Errore',
        content: 'Errore visualizzazione tracciato IRU'
      });
    }
  }

  // --------------------------------------------------------------------------------

  clearTracciatoIRUFromMap() {
    this._mapService.clearAllFeaturesFromTemporaryLayer(true);
  }
}
